import classNames from "classnames";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";
import { defineMessages } from "react-intl";

import { useFormatMessage } from "../Intl/Context";
import Button from "../Buttons/Button";
import Icon from "../Utils/Icon";
import useSpeechToTextRecorder, {
  RECORDING_WARNING_WINDOW_MS,
  STT_RECORDER_STATE_IDLE,
  STT_RECORDER_STATE_PAUSED,
  STT_RECORDER_STATE_RECORDING,
  STT_RECORDER_STATE_TRANSCRIBING
} from "./useSpeechToTextRecorder";
import SpeechToTextRecorderControls from "./SpeechToTextRecorderControls";
import SpeechToTextRecorderError from "./SpeechToTextRecorderError";

const formattedDuration = (durationMs, rounding = Math.floor) => {
  const totalSeconds = rounding(Math.max(0, durationMs) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const SpeechToTextRecorder = forwardRef(
  (
    {
      resourceId,
      onResult,
      onRecordingReady,
      onCancel,
      onError,
      onStateChange,
      disabled = false,
      className,
      autoStart = false
    },
    ref
  ) => {
    const t = useFormatMessage();
    const [errorCode, setErrorCode] = useState(null);
    const hasAutoStartedRef = useRef(false);

    const handleRecorderError = useCallback(
      nextErrorCode => {
        setErrorCode(nextErrorCode);
        onError?.(nextErrorCode);
      },
      [onError]
    );

    const {
      discardCurrentRecording,
      finalizeRecording,
      isMediaRecorderSupported,
      pauseRecording,
      recordingDurationMs,
      recordingDurationLimitMs,
      restartRecording,
      resumeRecording,
      startRecording,
      state
    } = useSpeechToTextRecorder({
      resourceId,
      onResult,
      onRecordingReady,
      onError: handleRecorderError,
      onStateChange,
      disabled
    });

    useImperativeHandle(
      ref,
      () => ({
        discardCurrentRecording
      }),
      [discardCurrentRecording]
    );

    const isIdle = state === STT_RECORDER_STATE_IDLE;
    const isRecording = state === STT_RECORDER_STATE_RECORDING;
    const isPaused = state === STT_RECORDER_STATE_PAUSED;
    const isTranscribing = state === STT_RECORDER_STATE_TRANSCRIBING;
    const isRecordingSessionActive = isRecording || isPaused;

    const startLabelMessage = t(intlMessages.startLabel);
    const startHintMessage = t(intlMessages.startHint);
    const processingMessage = t(intlMessages.transcriptionInProgress);
    const microphoneHelpLinkLabel = t(intlMessages.microphoneHelpLinkLabel);
    const restartConfirmMessage = t(intlMessages.restartConfirm);
    const cancelConfirmMessage = t(intlMessages.cancelConfirm);
    const cancelLabel = t(intlMessages.cancelLabel);
    // Help URL is translated like copy so each locale can point to its own support page.
    const microphoneHelpUrl = t(intlMessages.microphoneHelpUrl);
    const remainingRecordingDurationMs = Math.max(
      0,
      recordingDurationLimitMs - recordingDurationMs
    );
    const showDurationWarning =
      isRecordingSessionActive &&
      remainingRecordingDurationMs <= RECORDING_WARNING_WINDOW_MS;
    const durationWarningTime = formattedDuration(
      remainingRecordingDurationMs,
      Math.ceil
    );
    const durationWarningMessage = showDurationWarning
      ? t(intlMessages.durationWarning, { remainingTime: durationWarningTime })
      : null;
    const recordingDurationTime = formattedDuration(recordingDurationMs);

    // Hint is hidden during recording/transcription because active controls already
    // communicate the current state.
    const hintMessage =
      isTranscribing || isRecordingSessionActive ? "" : startHintMessage;

    const handleStartRecording = useCallback(() => {
      setErrorCode(null);
      startRecording();
    }, [startRecording]);

    const handleFinalizeRecording = useCallback(() => {
      setErrorCode(null);
      finalizeRecording();
    }, [finalizeRecording]);

    const handleRestartRecording = useCallback(() => {
      if (!window.confirm(restartConfirmMessage)) return;

      setErrorCode(null);
      restartRecording();
    }, [restartRecording, restartConfirmMessage]);

    const handlePauseToggle = useCallback(() => {
      if (isPaused) {
        resumeRecording();
      } else {
        pauseRecording();
      }
    }, [isPaused, pauseRecording, resumeRecording]);

    // The cancel button is always rendered so users have a guaranteed escape
    // hatch out of the recorder (otherwise they'd be forced to either send a
    // transcription or restart, both of which keep them inside the recorder).
    // The discard call clears any buffered audio before yielding control back
    // to the parent.
    const handleCancel = useCallback(() => {
      if (!window.confirm(cancelConfirmMessage)) return;

      setErrorCode(null);
      discardCurrentRecording();
      onCancel();
    }, [cancelConfirmMessage, discardCurrentRecording, onCancel]);

    // `autoStart` is used by callers that already gate visibility behind an
    // explicit user action (e.g. SpeechToTextRichTextInput mounts the recorder
    // only after the user clicks "Parler"). Mounting and starting are then a
    // single user gesture instead of two.
    //
    // The ref guard ensures we only auto-start once per mount: state transitions
    // (pause/resume) must not re-trigger recording after the user has stopped it.
    useEffect(() => {
      if (!autoStart) return;
      if (hasAutoStartedRef.current) return;
      if (disabled || !isMediaRecorderSupported) return;
      if (state !== STT_RECORDER_STATE_IDLE) return;

      hasAutoStartedRef.current = true;
      setErrorCode(null);
      startRecording();
    }, [autoStart, disabled, isMediaRecorderSupported, startRecording, state]);

    if (!isMediaRecorderSupported) return null;

    return (
      <div className={classNames("speech-to-text-recorder", className)}>
        <SpeechToTextRecorderError
          errorCode={errorCode}
          helpUrl={microphoneHelpUrl}
          helpLabel={microphoneHelpLinkLabel}
        />

        <div
          className={classNames("speech-to-text-recorder__panel", {
            "speech-to-text-recorder__panel--recording": isRecordingSessionActive,
            "speech-to-text-recorder__panel--processing": isTranscribing
          })}
        >
          {isRecordingSessionActive && (
            <Button
              type="button"
              className="speech-to-text-recorder__cancel"
              onClick={handleCancel}
              aria-label={cancelLabel}
              disabled={isTranscribing}
              iconClassName="fa fa-times"
            />
          )}

          {isRecordingSessionActive && (
            <div className="speech-to-text-recorder__timer">
              <span className="speech-to-text-recorder__timer-time">
                {recordingDurationTime}
              </span>
            </div>
          )}

          {durationWarningMessage && (
            <p className="speech-to-text-recorder__duration-warning">
              {durationWarningMessage}
            </p>
          )}

          {isTranscribing && (
            <div className="speech-to-text-recorder__processing-status">
              <Icon className="fa fa-circle-notch fa-spin speech-to-text-recorder__processing-icon" />
              <span>{processingMessage}</span>
            </div>
          )}

          {isIdle && (
            <Button
              type="button"
              className="speech-to-text-recorder__mic-button"
              onClick={handleStartRecording}
              disabled={disabled || !isMediaRecorderSupported}
              aria-label={startLabelMessage}
              iconClassName="fa fa-microphone speech-to-text-recorder__mic-icon"
            />
          )}

          {isRecordingSessionActive && (
            <SpeechToTextRecorderControls
              isPaused={isPaused}
              onPauseToggle={handlePauseToggle}
              onRestart={handleRestartRecording}
              onSend={handleFinalizeRecording}
            />
          )}

          {hintMessage && (
            <p
              className={classNames("speech-to-text-recorder__hint", {
                "speech-to-text-recorder__hint--recording": isRecordingSessionActive,
                "speech-to-text-recorder__hint--processing": isTranscribing
              })}
            >
              {hintMessage}
            </p>
          )}
        </div>
      </div>
    );
  }
);

SpeechToTextRecorder.displayName = "SpeechToTextRecorder";

export default SpeechToTextRecorder;

const intlMessages = defineMessages({
  startLabel: {
    id: "speechToTextRecorder.startLabel",
    defaultMessage: "Commencez l'enregistrement"
  },
  startHint: {
    id: "speechToTextRecorder.startHint",
    defaultMessage: "Appuyez sur le micro pour commencer."
  },
  transcriptionInProgress: {
    id: "speechToTextRecorder.transcriptionInProgress",
    defaultMessage: "Transcription en cours..."
  },
  durationWarning: {
    id: "speechToTextRecorder.durationWarning",
    defaultMessage: "Arrêt automatique dans {remainingTime}."
  },
  restartConfirm: {
    id: "speechToTextRecorder.restartConfirm",
    defaultMessage: "Voulez-vous recommencer ? L'enregistrement en cours sera supprimé."
  },
  cancelConfirm: {
    id: "speechToTextRecorder.cancelConfirm",
    defaultMessage: "Voulez-vous annuler ? L'enregistrement en cours sera supprimé."
  },
  cancelLabel: {
    id: "speechToTextRecorder.cancelLabel",
    defaultMessage: "Annuler l'enregistrement"
  },
  microphoneHelpLinkLabel: {
    id: "speechToTextRecorder.microphoneHelpLinkLabel",
    defaultMessage: "Guide d'aide micro"
  },
  microphoneHelpUrl: {
    id: "speechToTextRecorder.microphoneHelpUrl",
    defaultMessage:
      "https://support.meetingone.com/migration/fr/knowledge/re-enabling-your-microphone-in-browsers"
  }
});
